import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  FileText,
  GraduationCap,
  Users,
  Settings,
  LogOut,
  ArrowLeft,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

export function AdminLayout() {
  const { t } = useTranslation('admin');
  const { signOut } = useAuth();
  const location = useLocation();
  const { locale } = useParams();
  const currentLocale = locale || 'fr';

  const navItems: NavItem[] = [
    {
      name: t('nav.analytics'),
      href: `/${currentLocale}/admin/analytics`,
      icon: BarChart3,
    },
    {
      name: t('nav.articles'),
      href: `/${currentLocale}/admin/articles`,
      icon: FileText,
    },
    {
      name: t('nav.formations'),
      href: `/${currentLocale}/admin/formations`,
      icon: GraduationCap,
    },
    {
      name: t('nav.users'),
      href: `/${currentLocale}/admin/users`,
      icon: Users,
    },
    {
      name: t('nav.settings'),
      href: `/${currentLocale}/admin/settings`,
      icon: Settings,
    },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="flex h-screen bg-stone-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Logo & Title */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-aspexit-green" />
            <span className="font-bold text-xl text-gray-900">Aspexit</span>
          </div>
          <p className="text-sm text-gray-600">Admin Dashboard</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-aspexit-green/10 text-aspexit-green'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          {/* Back to Site */}
          <Button
            variant="outline"
            className="w-full justify-start"
            asChild
          >
            <Link to={`/${currentLocale}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('nav.backToSite')}
            </Link>
          </Button>

          {/* Logout */}
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleSignOut}
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
